import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { Confirm } from '../../../Commons/Modals/Confirm';
import {
    GetSuggestions as BLGetSuggestions,
    GetActivePlaylist as BLGetActivePlaylist,
    GetSettings as BLGetSettings,
    GetMetrics as BLGetMetrics,
    UpdateSettings as BLUpdateSettings,
    CreateManualSong as BLCreateManualSong,
    ToggleFavorite as BLToggleFavorite,
    ToggleArchive as BLToggleArchive,
    DeleteSuggestion as BLDeleteSuggestion,
    ReorderPlaylist as BLReorderPlaylist,
    SetNowPlaying as BLSetNowPlaying,
    TogglePlayed as BLTogglePlayed,
    ToggleSubmissionActive as BLToggleSubmissionActive,
    ResetDynamic as BLResetDynamic,
    ExportPlaylistToExcel
} from '../../../../utilities/BL/Admin/Overlays/Playlist';
import {
    ConnectWebSocketForPlaylistAdmin,
    DisconnectWebSocketForPlaylistAdmin
} from '../../../../utilities/WS/Admin/PlaylistWS';
import { PlaylistMetricsCards } from './PlaylistMetricsCards';
import { PlaylistSuggestionsTable } from './PlaylistSuggestionsTable';
import { PlaylistActiveList } from './PlaylistActiveList';
import { PlaylistCriticalControls } from './PlaylistCriticalControls';
import { PlaylistManualSongModal } from './PlaylistManualSongModal';
import { PlaylistSettingsModal } from './PlaylistSettingsModal';
import { PlaylistThemeModal } from './PlaylistThemeModal';

/**
 * CONSOLA GENERAL DEL ESTADIO (CONTROL GENERAL)
 * [HU-06] Arma la Playlist
 * 
 * Consola centralizada de administración y monitoreo para el Operador General del Estadio.
 * Cuenta con:
 * - Indicadores KPIs en tiempo real (PlaylistMetricsCards).
 * - Vista dual desacoplada 50/50 con scrollbars independientes (PlaylistSuggestionsTable + PlaylistActiveList).
 * - Conmutador de visibilidad del Overlay en pantallas del estadio.
 * - Exportación de reportes operativos a Excel (.xlsx).
 * - Acordeón inferior para controles críticos (apertura de peticiones y reinicio de sala).
 * - Comunicación bidireccional en tiempo real con SignalR.
 */
export const PlaylistBoard = ({
    getOutOfTheDynamic = () => {},
    roomId: propRoomId = null
}) => {
    const { showMessage } = useFloatMessage();

    // -------------------------------------------------------------------------
    // 1. RESOLUCIÓN DE IDENTIFICADOR DE SALA (ROOM ID)
    // -------------------------------------------------------------------------
    const roomId = useMemo(() => {
        if (propRoomId) return propRoomId;
        const searchParams = new URLSearchParams(window.location.search);
        const urlRoomId = searchParams.get('roomId') || searchParams.get('room');
        if (urlRoomId) return urlRoomId;
        const storedRoomId = localStorage.getItem('interaktia_room_id') || localStorage.getItem('roomId');
        return storedRoomId || '';
    }, [propRoomId]);

    // -------------------------------------------------------------------------
    // 2. ESTADOS PRINCIPALES DE DATOS
    // -------------------------------------------------------------------------
    const [suggestions, setSuggestions] = useState([]);
    const [activePlaylist, setActivePlaylist] = useState([]);
    const [settings, setSettings] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [nowPlayingSong, setNowPlayingSong] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [wsStatus, setWsStatus] = useState('connecting'); // 'connecting', 'connected', 'reconnecting', 'error', 'closed'

    // -------------------------------------------------------------------------
    // 3. ESTADOS DE MODALES
    // -------------------------------------------------------------------------
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        actionToYesButton: async () => ({ success: true }),
        textYesButton: 'Sí, confirmar',
        textNoButton: 'Cancelar',
        confirmData: null
    });

    const [showManualModal, setShowManualModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showThemeModal, setShowThemeModal] = useState(false);

    // -------------------------------------------------------------------------
    // 4. CARGA INICIAL Y MANUAL DE DATOS (BL)
    // -------------------------------------------------------------------------
    const loadInitialData = useCallback(async (showNotification = false) => {
        setIsLoading(true);
        try {
            const [suggestionsRes, activeRes, settingsRes, metricsRes] = await Promise.all([
                BLGetSuggestions({ roomId, includeArchived: true }),
                BLGetActivePlaylist({ roomId }),
                BLGetSettings({ roomId }),
                BLGetMetrics({ roomId })
            ]);

            if (suggestionsRes.success && Array.isArray(suggestionsRes.data)) {
                setSuggestions(suggestionsRes.data);
            }

            if (activeRes.success && Array.isArray(activeRes.data)) {
                setActivePlaylist(activeRes.data);
                const currentLive = activeRes.data.find(item => item.isNowPlaying);
                setNowPlayingSong(currentLive || null);
            }

            if (settingsRes.success && settingsRes.data) {
                setSettings(settingsRes.data);
            }

            if (metricsRes.success && metricsRes.data) {
                setMetrics(metricsRes.data);
            }

            if (showNotification) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '🔄 Consola de Estadio actualizada con éxito.'
                });
            }
        } catch (error) {
            console.error('[PlaylistBoard] Error al cargar datos iniciales:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al consultar datos de la consola de Playlist.'
            });
        } finally {
            setIsLoading(false);
        }
    }, [roomId, showMessage]);

    // -------------------------------------------------------------------------
    // 5. CONEXIÓN SIGNALR EN TIEMPO REAL
    // -------------------------------------------------------------------------
    useEffect(() => {
        loadInitialData(false);

        const handleWsMessage = (envelope) => {
            if (!envelope) return;
            const eventType = envelope.type || '';
            const payload = envelope.payload || envelope.data;

            console.log(`[PlaylistBoard] WS Event: "${eventType}"`, payload);

            switch (eventType) {
                case 'suggestion_received': {
                    if (!payload || !payload.id) return;
                    setSuggestions((prev) => {
                        const exists = prev.some(item => item.id === payload.id);
                        if (exists) {
                            return prev.map(item => item.id === payload.id ? { ...item, ...payload } : item);
                        }
                        return [payload, ...prev];
                    });

                    if (payload.isFavorite) {
                        setActivePlaylist((prev) => {
                            const exists = prev.some(item => item.id === payload.id);
                            return exists ? prev : [...prev, payload];
                        });
                    }

                    // Actualizar métricas reactivamente
                    setMetrics((prev) => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            totalSuggestions: (prev.totalSuggestions || 0) + 1
                        };
                    });

                    showMessage({
                        type: MESSAGE_TYPE.INFO,
                        text: `📥 Nueva petición: "${payload.title}"`
                    });
                    break;
                }

                case 'vote_added': {
                    if (!payload || !payload.suggestionId) return;
                    const suggestionId = payload.suggestionId;
                    const newVoteCount = payload.newVoteCount || payload.voteCount || 1;

                    setSuggestions(prev =>
                        prev.map(item =>
                            item.id === suggestionId ? { ...item, voteCount: newVoteCount } : item
                        )
                    );

                    setActivePlaylist(prev =>
                        prev.map(item =>
                            item.id === suggestionId ? { ...item, voteCount: newVoteCount } : item
                        )
                    );

                    setMetrics(prev => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            totalVotes: (prev.totalVotes || 0) + 1
                        };
                    });
                    break;
                }

                case 'suggestion_updated': {
                    if (!payload || !payload.id) return;
                    setSuggestions(prev =>
                        prev.map(item => item.id === payload.id ? { ...item, ...payload } : item)
                    );

                    setActivePlaylist(prev => {
                        if (payload.isFavorite) {
                            const exists = prev.some(item => item.id === payload.id);
                            return exists
                                ? prev.map(item => item.id === payload.id ? { ...item, ...payload } : item)
                                : [...prev, payload];
                        } else {
                            return prev.filter(item => item.id !== payload.id);
                        }
                    });

                    if (payload.isNowPlaying !== undefined) {
                        if (payload.isNowPlaying) {
                            setNowPlayingSong(payload);
                        } else {
                            setNowPlayingSong(curr => (curr && curr.id === payload.id ? null : curr));
                        }
                    }
                    break;
                }

                case 'suggestion_archived': {
                    if (!payload || !payload.suggestionId) return;
                    const { suggestionId, isArchived } = payload;
                    setSuggestions(prev =>
                        prev.map(item =>
                            item.id === suggestionId ? { ...item, isArchived } : item
                        )
                    );
                    if (isArchived) {
                        setActivePlaylist(prev => prev.filter(item => item.id !== suggestionId));
                        setNowPlayingSong(curr => (curr && curr.id === suggestionId ? null : curr));
                    }
                    break;
                }

                case 'suggestion_deleted': {
                    if (!payload || !payload.suggestionId) return;
                    const { suggestionId } = payload;
                    setSuggestions(prev => prev.filter(item => item.id !== suggestionId));
                    setActivePlaylist(prev => prev.filter(item => item.id !== suggestionId));
                    setNowPlayingSong(curr => (curr && curr.id === suggestionId ? null : curr));
                    break;
                }

                case 'playlist_updated': {
                    if (Array.isArray(payload)) {
                        setActivePlaylist(payload);
                        const live = payload.find(item => item.isNowPlaying);
                        setNowPlayingSong(live || null);
                    }
                    break;
                }

                case 'now_playing_changed': {
                    if (payload && payload.id) {
                        setNowPlayingSong(payload);
                        setActivePlaylist(prev =>
                            prev.map(item => ({ ...item, isNowPlaying: item.id === payload.id }))
                        );
                        setSuggestions(prev =>
                            prev.map(item => ({ ...item, isNowPlaying: item.id === payload.id }))
                        );
                    } else {
                        setNowPlayingSong(null);
                        setActivePlaylist(prev =>
                            prev.map(item => ({ ...item, isNowPlaying: false }))
                        );
                        setSuggestions(prev =>
                            prev.map(item => ({ ...item, isNowPlaying: false }))
                        );
                    }
                    break;
                }

                case 'metrics_updated': {
                    if (payload) {
                        setMetrics(payload);
                    }
                    break;
                }

                case 'settings_updated': {
                    if (payload) {
                        setSettings(payload);
                    }
                    break;
                }

                case 'dynamic_reset': {
                    showMessage({
                        type: MESSAGE_TYPE.WARNING,
                        text: '⚠️ La dinámica fue reiniciada en el estadio.'
                    });
                    loadInitialData(false);
                    break;
                }

                default:
                    break;
            }
        };

        ConnectWebSocketForPlaylistAdmin(
            handleWsMessage,
            (newStatus) => setWsStatus(newStatus),
            roomId
        );

        return () => {
            DisconnectWebSocketForPlaylistAdmin();
        };
    }, [roomId, loadInitialData, showMessage]);

    // -------------------------------------------------------------------------
    // 6. CONTROL CONMUTADOR DE OVERLAY EN PANTALLA (📺)
    // -------------------------------------------------------------------------
    const isOverlayVisible = settings ? Boolean(settings.isOverlayVisible) : false;

    const handleToggleOverlay = async () => {
        const nextVisible = !isOverlayVisible;
        const currentSettings = settings || {};

        // Optimistic update
        setSettings(prev => prev ? { ...prev, isOverlayVisible: nextVisible } : { isOverlayVisible: nextVisible });

        try {
            const res = await BLUpdateSettings({
                roomId,
                ...currentSettings,
                isOverlayVisible: nextVisible
            });

            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: nextVisible
                        ? '📺 Overlay en pantalla ACTIVADO'
                        : '📺 Overlay en pantalla DESACTIVADO'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'Error al cambiar visibilidad del Overlay.'
                });
                loadInitialData(false);
            }
        } catch (error) {
            console.error('[PlaylistBoard] Error al cambiar estado del Overlay:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado al conmutar Overlay.'
            });
            loadInitialData(false);
        }
    };

    // -------------------------------------------------------------------------
    // 7. EXPORTACIÓN A EXCEL (.xlsx)
    // -------------------------------------------------------------------------
    const handleExportExcel = () => {
        try {
            const result = ExportPlaylistToExcel(suggestions, activePlaylist, 'Estadio');
            if (result.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `📊 ${result.message}`
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: result.message || 'No se pudo exportar el archivo Excel.'
                });
            }
        } catch (error) {
            console.error('[PlaylistBoard] Error al invocar ExportPlaylistToExcel:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado al generar el archivo Excel.'
            });
        }
    };

    // -------------------------------------------------------------------------
    // 8. GESTIÓN DE SUGERENCIAS Y PLAYLIST ACTIVA
    // -------------------------------------------------------------------------
    const handleToggleFavorite = async (suggestionId, isFavorite) => {
        try {
            setSuggestions(prev =>
                prev.map(item => item.id === suggestionId ? { ...item, isFavorite } : item)
            );

            const res = await BLToggleFavorite({ roomId, suggestionId, isFavorite });
            if (res.success) {
                if (isFavorite) {
                    const target = suggestions.find(s => s.id === suggestionId);
                    if (target) {
                        setActivePlaylist(prev => {
                            const exists = prev.some(item => item.id === suggestionId);
                            return exists ? prev : [...prev, { ...target, isFavorite: true }];
                        });
                    }
                } else {
                    setActivePlaylist(prev => prev.filter(item => item.id !== suggestionId));
                }
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: isFavorite ? '⭐ Añadida a la Playlist Oficial' : 'Retirada de la Playlist Oficial'
                });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al actualizar favorito.' });
                loadInitialData(false);
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al actualizar favorito.' });
            loadInitialData(false);
        }
    };

    const handleToggleArchive = async (suggestionId, isArchived) => {
        try {
            setSuggestions(prev =>
                prev.map(item => item.id === suggestionId ? { ...item, isArchived } : item)
            );
            if (isArchived) {
                setActivePlaylist(prev => prev.filter(item => item.id !== suggestionId));
            }

            const res = await BLToggleArchive({ roomId, suggestionId, isArchived });
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: isArchived ? '📦 Pista archivada' : '📦 Pista desarchivada'
                });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al archivar pista.' });
                loadInitialData(false);
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al archivar pista.' });
            loadInitialData(false);
        }
    };

    const handleDeleteSuggestion = (suggestionId, title = '') => {
        setConfirmConfig({
            message: `¿Estás seguro de que deseas eliminar permanentemente la pista "${title || 'seleccionada'}"?`,
            textYesButton: 'Sí, Eliminar',
            textNoButton: 'Cancelar',
            actionToYesButton: async () => {
                const res = await BLDeleteSuggestion({ roomId, suggestionId, softDelete: true });
                if (res.success) {
                    setSuggestions(prev => prev.filter(item => item.id !== suggestionId));
                    setActivePlaylist(prev => prev.filter(item => item.id !== suggestionId));
                    setNowPlayingSong(curr => (curr && curr.id === suggestionId ? null : curr));
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '🗑️ Pista eliminada con éxito.' });
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al eliminar pista.' });
                }
                return res;
            },
            confirmData: { suggestionId }
        });
        setShowConfirmModal(true);
    };

    const handleRemoveFromActive = (suggestionId, title = '') => {
        setConfirmConfig({
            message: `¿Deseas retirar "${title || 'esta pista'}" de la Playlist Oficial del estadio?`,
            textYesButton: 'Sí, Quitar',
            textNoButton: 'Cancelar',
            actionToYesButton: async () => {
                const res = await BLToggleFavorite({ roomId, suggestionId, isFavorite: false });
                if (res.success) {
                    setActivePlaylist(prev => prev.filter(item => item.id !== suggestionId));
                    setSuggestions(prev =>
                        prev.map(item => item.id === suggestionId ? { ...item, isFavorite: false } : item)
                    );
                    setNowPlayingSong(curr => (curr && curr.id === suggestionId ? null : curr));
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Pista retirada de la playlist activa.' });
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al retirar pista.' });
                }
                return res;
            },
            confirmData: { suggestionId }
        });
        setShowConfirmModal(true);
    };

    const handleSetNowPlaying = async (suggestionId) => {
        try {
            const res = await BLSetNowPlaying({ roomId, suggestionId });
            if (res.success) {
                if (suggestionId) {
                    const target = activePlaylist.find(item => item.id === suggestionId) || suggestions.find(s => s.id === suggestionId);
                    setNowPlayingSong(target || null);
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: `▶️ EN VIVO EN ESTADIO: "${target?.title || 'Canción'}"`
                    });
                } else {
                    setNowPlayingSong(null);
                    showMessage({
                        type: MESSAGE_TYPE.INFO,
                        text: '⏹️ Reproducción en vivo detenida.'
                    });
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al cambiar pista en vivo.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al cambiar pista en vivo.' });
        }
    };

    const handleTogglePlayed = async (suggestionId, isPlayed) => {
        try {
            setActivePlaylist(prev =>
                prev.map(item => item.id === suggestionId ? { ...item, isPlayed } : item)
            );
            setSuggestions(prev =>
                prev.map(item => item.id === suggestionId ? { ...item, isPlayed } : item)
            );

            const res = await BLTogglePlayed({ roomId, suggestionId, isPlayed });
            if (!res.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al actualizar estado reproducida.' });
                loadInitialData(false);
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cambiar estado reproducida.' });
            loadInitialData(false);
        }
    };

    const handleReorderPlaylist = async (orderedIds) => {
        try {
            const mapById = new Map(activePlaylist.map(item => [item.id, item]));
            const newOrdered = orderedIds.map(id => mapById.get(id)).filter(Boolean);
            setActivePlaylist(newOrdered);

            const res = await BLReorderPlaylist({ roomId, orderedIds });
            if (!res.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al guardar el nuevo orden.' });
                loadInitialData(false);
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al reordenar la playlist.' });
            loadInitialData(false);
        }
    };

    // -------------------------------------------------------------------------
    // 9. CONTROLES CRÍTICOS (RECEPCIÓN & REINICIO)
    // -------------------------------------------------------------------------
    const handleToggleSubmissionActive = async () => {
        const currentActive = settings ? settings.isSubmissionActive : true;
        const newActive = !currentActive;
        try {
            const res = await BLToggleSubmissionActive({ roomId, isActive: newActive });
            if (res.success) {
                setSettings(prev => prev ? { ...prev, isSubmissionActive: newActive } : { isSubmissionActive: newActive });
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: newActive
                        ? '🟢 Recepción de sugerencias ABIERTA a aficionados'
                        : '🔴 Recepción de sugerencias PAUSADA en el estadio'
                });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al modificar recepción.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al actualizar recepción.' });
        }
    };

    const handleResetDynamic = () => {
        setConfirmConfig({
            message: '⚠️ ¿Estás seguro de que deseas REINICIAR LA DINÁMICA del estadio? Todas las sugerencias activas se archivarán y la sala quedará lista para el siguiente partido.',
            textYesButton: 'Sí, Reiniciar a Ceros',
            textNoButton: 'Cancelar',
            actionToYesButton: async () => {
                const res = await BLResetDynamic({ roomId });
                if (res.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '🔄 Dinámica reiniciada con éxito a ceros.' });
                    await loadInitialData(false);
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al reiniciar dinámica.' });
                }
                return res;
            },
            confirmData: {}
        });
        setShowConfirmModal(true);
    };

    const activePlaylistIds = useMemo(() => {
        return activePlaylist.map(item => item.id);
    }, [activePlaylist]);

    return (
        <div className="PlaylistBoard">
            {/* --- HEADER PRINCIPAL DE CONTROL GENERAL --- */}
            <header className="PlaylistBoard__Header">
                <div className="PlaylistBoard__HeaderLeft">
                    <button
                        type="button"
                        className="PlaylistBoard__BackBtn"
                        onClick={getOutOfTheDynamic}
                        title="Regresar al Dashboard"
                    >
                        <i className="bi bi-arrow-left"></i>
                    </button>

                    <div className="PlaylistBoard__Branding">
                        <div className="d-flex align-items-center gap-2">
                            <span className="PlaylistBoard__Icon">🎵</span>
                            <h1 className="PlaylistBoard__Title">Arma la Playlist - Control General del Estadio</h1>
                            <span className="PlaylistBoard__Badge">Control General</span>
                        </div>
                        <span className="PlaylistBoard__Subtitle">
                            Monitoreo en tiempo real, control de pantalla y gestión de dinámicas
                        </span>
                    </div>
                </div>

                <div className="PlaylistBoard__HeaderCenter">
                    <div className={`PlaylistBoard__WsBadge PlaylistBoard__WsBadge--${wsStatus}`} title={`Estado SignalR: ${wsStatus}`}>
                        <span className="PlaylistBoard__WsDot"></span>
                        <span className="PlaylistBoard__WsText">
                            {wsStatus === 'connected' && 'SignalR En Vivo'}
                            {wsStatus === 'connecting' && 'Conectando...'}
                            {wsStatus === 'reconnecting' && 'Reconectando...'}
                            {wsStatus === 'error' && 'Sin Conexión'}
                            {wsStatus === 'closed' && 'Desconectado'}
                        </span>
                    </div>
                </div>

                <div className="PlaylistBoard__HeaderRight">
                    {/* Botón Alta Rápida (+) */}
                    <button
                        type="button"
                        className="PlaylistBoard__HeaderBtn PlaylistBoard__HeaderBtn--Add"
                        onClick={() => setShowManualModal(true)}
                        title="Alta Rápida de Canción / Artista"
                    >
                        <i className="bi bi-plus-lg"></i>
                        <span className="d-none d-xl-inline">Alta Rápida</span>
                    </button>

                    {/* Botón Tema y Fondos (🎨) */}
                    <button
                        type="button"
                        className="PlaylistBoard__HeaderBtn"
                        onClick={() => setShowThemeModal(true)}
                        title="Personalizar Tema y Fondos Visuales"
                    >
                        <i className="bi bi-palette-fill text-info"></i>
                        <span className="d-none d-lg-inline">Tema y Fondos</span>
                    </button>

                    {/* Botón Configuración (⚙️) */}
                    <button
                        type="button"
                        className="PlaylistBoard__HeaderBtn"
                        onClick={() => setShowSettingsModal(true)}
                        title="Configuración de Dinámica"
                    >
                        <i className="bi bi-gear-fill text-warning"></i>
                        <span className="d-none d-lg-inline">Configuración</span>
                    </button>

                    {/* Botón Conmutador de Overlay (📺) */}
                    <button
                        type="button"
                        className={`PlaylistBoard__HeaderBtn ${isOverlayVisible ? 'PlaylistBoard__HeaderBtn--OverlayActive' : 'PlaylistBoard__HeaderBtn--OverlayInactive'}`}
                        onClick={handleToggleOverlay}
                        title={isOverlayVisible ? 'Ocultar Overlay en Pantalla' : 'Mostrar Overlay en Pantalla'}
                        disabled={isLoading}
                    >
                        <i className={`bi ${isOverlayVisible ? 'bi-tv-fill text-success' : 'bi-tv text-secondary'}`}></i>
                        <span className="d-none d-sm-inline">
                            {isOverlayVisible ? 'Overlay: Activo' : 'Overlay: Inactivo'}
                        </span>
                    </button>

                    {/* Botón Exportar a Excel */}
                    <button
                        type="button"
                        className="PlaylistBoard__HeaderBtn PlaylistBoard__HeaderBtn--Excel"
                        onClick={handleExportExcel}
                        title="Exportar reporte de peticiones y playlist a Excel (.xlsx)"
                    >
                        <i className="bi bi-file-earmark-excel-fill text-success"></i>
                        <span className="d-none d-lg-inline">Exportar Excel</span>
                    </button>

                    {/* Botón Recarga Manual */}
                    <button
                        type="button"
                        className="PlaylistBoard__HeaderBtn"
                        onClick={() => loadInitialData(true)}
                        disabled={isLoading}
                        title="Recargar datos del servidor"
                    >
                        <i className={`bi bi-arrow-clockwise ${isLoading ? 'PlaylistBoard__Spin' : ''}`}></i>
                    </button>
                </div>
            </header>

            {/* --- CUERPO PRINCIPAL DE LA CONSOLA --- */}
            <div className="PlaylistBoard__Content">
                {/* 1. SECCIÓN SUPERIOR: METRIC CARDS */}
                <div className="PlaylistBoard__MetricsSection">
                    <PlaylistMetricsCards
                        metrics={metrics}
                        suggestions={suggestions}
                        activePlaylist={activePlaylist}
                    />
                </div>

                {/* 2. SECCIÓN CENTRAL: TABLAS DUALES 50/50 CON SCROLL INDEPENDIENTE */}
                <div className="PlaylistBoard__TablesSection">
                    <div className="PlaylistBoard__TablesDual">
                        {/* BANDEJA DE PETICIONES */}
                        <div className="PlaylistBoard__TableColumn PlaylistBoard__TableColumn--Left">
                            <PlaylistSuggestionsTable
                                suggestions={suggestions}
                                activePlaylistIds={activePlaylistIds}
                                nowPlayingId={nowPlayingSong ? nowPlayingSong.id : null}
                                onToggleFavorite={handleToggleFavorite}
                                onToggleArchive={handleToggleArchive}
                                onDeleteSuggestion={handleDeleteSuggestion}
                                onOpenManualModal={() => setShowManualModal(true)}
                                onSuggestionUpdated={(updated) => {
                                    setSuggestions(prev =>
                                        prev.map(item => item.id === updated.id ? { ...item, ...updated } : item)
                                    );
                                    setActivePlaylist(prev =>
                                        prev.map(item => item.id === updated.id ? { ...item, ...updated } : item)
                                    );
                                }}
                                roomId={roomId}
                                showMessage={showMessage}
                                isLoading={isLoading}
                            />
                        </div>

                        {/* PLAYLIST OFICIAL / COLA ACTIVA */}
                        <div className="PlaylistBoard__TableColumn PlaylistBoard__TableColumn--Right">
                            <PlaylistActiveList
                                activePlaylist={activePlaylist}
                                nowPlayingSong={nowPlayingSong}
                                onSetNowPlaying={handleSetNowPlaying}
                                onTogglePlayed={handleTogglePlayed}
                                onRemoveFromActive={handleRemoveFromActive}
                                onReorderPlaylist={handleReorderPlaylist}
                                showMessage={showMessage}
                                isLoading={isLoading}
                            />
                        </div>
                    </div>
                </div>

                {/* 3. SECCIÓN INFERIOR: CONTROLES CRÍTICOS COLAPSABLES */}
                <div className="PlaylistBoard__CriticalSection">
                    <PlaylistCriticalControls
                        settings={settings}
                        onToggleSubmission={handleToggleSubmissionActive}
                        onResetDynamic={handleResetDynamic}
                        isLoading={isLoading}
                    />
                </div>
            </div>

            {/* --- MODAL DE CONFIRMACIÓN (Confirm.js) --- */}
            {showConfirmModal && (
                <Confirm
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.actionToYesButton}
                    textYesButton={confirmConfig.textYesButton}
                    textNoButton={confirmConfig.textNoButton}
                    setShowModal={setShowConfirmModal}
                    setConfirmData={() => {}}
                />
            )}

            {/* --- MODAL PARA AGREGAR CANCIÓN MANUAL (ALTA RÁPIDA) --- */}
            {showManualModal && (
                <PlaylistManualSongModal
                    isOpen={showManualModal}
                    onClose={() => setShowManualModal(false)}
                    onSuccess={(newSong) => {
                        setSuggestions(prev => [newSong, ...prev]);
                        if (newSong.isFavorite) {
                            setActivePlaylist(prev => [...prev, newSong]);
                        }
                    }}
                    roomId={roomId}
                    defaultOperatorName="Control General"
                />
            )}

            {/* --- MODAL DE CONFIGURACIÓN OPERATIVA --- */}
            {showSettingsModal && (
                <PlaylistSettingsModal
                    isOpen={showSettingsModal}
                    settings={settings}
                    onClose={() => setShowSettingsModal(false)}
                    onSuccess={(updatedSettings) => {
                        setSettings(updatedSettings);
                    }}
                    roomId={roomId}
                />
            )}

            {/* --- MODAL DE TEMA Y FONDOS --- */}
            {showThemeModal && (
                <PlaylistThemeModal
                    isOpen={showThemeModal}
                    settings={settings}
                    onClose={() => setShowThemeModal(false)}
                    onSuccess={(updatedSettings) => {
                        setSettings(updatedSettings);
                    }}
                    roomId={roomId}
                />
            )}
        </div>
    );
};

PlaylistBoard.propTypes = {
    getOutOfTheDynamic: PropTypes.func,
    roomId: PropTypes.string
};

export default PlaylistBoard;
